/**
 * Save-model helpers for `ChatbotSettingsPage` — which fields belong to which card (for the
 * "Customise for <channel>" / "Reset to general" affordances, design §7.3), the dirty-fields-only
 * diff for the general row's merge-semantic `PUT` (§11), the pending-reset model (§17.1.4) and the
 * always-on-command preservation rule (§8.2, §17.3.1 — the codes now come from the dictionary,
 * never a hard-coded `'start'` literal).
 */
import type { ChatbotSettingsFields, ChatbotGeneralSettings } from './types';

/**
 * The Behaviour card's fields (design §8.1, §8.2, §8.4-8.12). **8.3 "Menu order" is deliberately
 * absent** — the hub stores `menu` but never reads it (`ConversationSettingsAdminService`,
 * verified 2026-09-18), so no editor renders it and no save ever includes it; see the user story's
 * Notes for the full disclosure of this deviation from the 2026-09-10 design.
 */
export const BEHAVIOUR_CARD_FIELDS: (keyof ChatbotSettingsFields)[] = [
  'greetingFlow',
  'enabledCommands',
  'languagePolicy',
  'closeAfterIdleDays',
  'retentionDays',
  'episodeIdleTimeoutMinutes',
  'idleGraceMinutes',
  'quoteInboundInReplies',
  'identityNegativeCacheTtlHours',
  'unsupportedReplyCooldownSeconds',
  'deliveryMaxRedispatch',
];

/** The fields inside the Behaviour card's collapsed "Advanced delivery & housekeeping" group (§8.10-8.12). */
export const BEHAVIOUR_ADVANCED_FIELDS: (keyof ChatbotSettingsFields)[] = [
  'identityNegativeCacheTtlHours',
  'unsupportedReplyCooldownSeconds',
  'deliveryMaxRedispatch',
];

/** The Escalation & AI card's fields (design §9.2-9.13). `handoffTargetRef` is never shown (Q8). */
export const ESCALATION_CARD_FIELDS: (keyof ChatbotSettingsFields)[] = [
  'aiMode',
  'confidenceThreshold',
  'lowConfidenceMaxStreak',
  'loopMaxUnresolvedTurns',
  'handoffEnabled',
  'escalationQueueTimeoutMinutes',
  'workingHours',
  'afterHoursPolicy',
  'escalationPolicyTopics',
  'interimAckAfterMs',
  'agentDeadlineMs',
  'toolAllowlist',
];

/**
 * `alwaysOnCodes` (the dictionary's `commands[].alwaysOn` codes — design §17.3.1) are never
 * checkboxes but must survive every save: the hub always answers them regardless of the saved set.
 * Always written first, whatever the loaded/draft array contained (including a duplicate, or none
 * at all). Falls back to `['start']` when the dictionary has not loaded yet, so a save mid-load
 * never silently drops the one code every tenant needs (`start`, unconditionally always-on on the
 * hub itself).
 */
export function normalizeEnabledCommands(
  commands: string[],
  alwaysOnCodes: readonly string[] = ['start'],
): string[] {
  const alwaysOn = alwaysOnCodes.length > 0 ? alwaysOnCodes : ['start'];
  return [...alwaysOn, ...commands.filter((command) => !alwaysOn.includes(command))];
}

/** A stable (key-sorted) JSON projection — the equality check shared by every dirty/diff helper in
 * this feature (`changedFields`, the channel-override controller, the provenance model). */
export function stableStringify(value: unknown): string {
  return JSON.stringify(value, (_key, val) => {
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      return Object.keys(val as Record<string, unknown>)
        .sort()
        .reduce<Record<string, unknown>>((acc, key) => {
          acc[key] = (val as Record<string, unknown>)[key];
          return acc;
        }, {});
    }
    return val;
  });
}

/** Structural equality via {@link stableStringify} — tolerant of key order, intolerant of anything else. */
export function stableEqual(a: unknown, b: unknown): boolean {
  return stableStringify(a) === stableStringify(b);
}

/**
 * The general row's `PUT` is merge-semantic (design A6/§11): an absent field means "unchanged", so
 * sending the whole resolved form back would materialise every untouched platform default into the
 * tenant's row. Returns only the fields whose value actually differs from `baseline` — mirrors
 * `useSettingsForm`'s own dirty-check technique so "differs" means the same thing in both places.
 * `enabledCommands` is normalised (see {@link normalizeEnabledCommands}) whenever it is part of the
 * diff, so an always-on code is never dropped by a save that touches the field.
 */
export function changedFields<T extends object>(
  draft: T,
  baseline: T | undefined,
  alwaysOnCommandCodes?: readonly string[],
): Partial<T> {
  const result: Partial<T> = {};
  for (const key of Object.keys(draft) as (keyof T)[]) {
    const draftValue = draft[key];
    const baselineValue = baseline?.[key];
    if (!baseline || !stableEqual(draftValue, baselineValue)) {
      result[key] = draftValue;
    }
  }
  if ('enabledCommands' in result) {
    (result as Partial<ChatbotSettingsFields>).enabledCommands = normalizeEnabledCommands(
      (result as Partial<ChatbotSettingsFields>).enabledCommands ?? [],
      alwaysOnCommandCodes,
    );
  }
  return result;
}

/**
 * The general save body (design §17.1.4): the dirty-fields diff, minus every field with a pending
 * reset (a reset never also appears as a "set" value — that would be the hub's unreachable "both set
 * and reset" 422), plus `resetFields` for the pending set. `pendingResets` is `keyof
 * ChatbotSettingsFields` — string keys read back off `ChatbotSettingsFields` cleanly because every
 * property of that interface is itself a plain, JSON-serialisable value.
 *
 * **Invariant (tested in `formModel.test.ts`): `resetFields` and `Object.keys(diff)` never intersect.**
 */
export function buildGeneralUpdate(
  draft: ChatbotSettingsFields,
  loaded: ChatbotGeneralSettings | undefined,
  pendingResets: ReadonlySet<keyof ChatbotSettingsFields>,
  alwaysOnCommandCodes?: readonly string[],
): Partial<ChatbotSettingsFields> & { resetFields?: string[] } {
  const diff = changedFields(draft, loaded, alwaysOnCommandCodes);
  for (const field of pendingResets) {
    delete (diff as Partial<Record<keyof ChatbotSettingsFields, unknown>>)[field];
  }
  return pendingResets.size > 0 ? { ...diff, resetFields: [...pendingResets] } : diff;
}

/** Removes exactly one field from a sparse override draft ("Use the general value for <field>"). */
export function dropField<T extends object>(obj: T, key: keyof T): T {
  const next = { ...obj };
  delete next[key];
  return next;
}

/** Removes every field of a card from a sparse override draft ("Reset to general" — card-scoped). */
export function dropCardFields<T extends object>(obj: T, fields: (keyof T)[]): T {
  const next = { ...obj };
  for (const field of fields) {
    delete next[field];
  }
  return next;
}
